import React from "react";
import ReactDOM from "react-dom/client";
import { RecoilRoot } from "recoil";

import "./index.css";
import reportWebVitals from "./reportWebVitals";

import { createBrowserRouter, RouterProvider } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import { RouterInfo } from "./routes";
import { applySizeClass, SIZE_MEDIUM } from "./utils/fontSize";

const Routers = createBrowserRouter(RouterInfo);

// RN 웹뷰 최초 로드 URL의 size 쿼리파라미터(medium/large/xlarge)를 읽어
// document.documentElement에 size-* 클래스를 부여한다. (기본값: medium)
const initialSizeParams = new URLSearchParams(window.location.search);
applySizeClass(initialSizeParams.get("size"), { fallback: SIZE_MEDIUM });

// 500번대 에러 상태 코드와 설명 매핑
const SERVER_ERROR_TYPES = {
  500: { name: "internal_server_error", description: "Internal Server Error" },
  501: { name: "not_implemented", description: "Not Implemented" },
  502: { name: "bad_gateway", description: "Bad Gateway" },
  503: { name: "service_unavailable", description: "Service Unavailable" },
  504: { name: "gateway_timeout", description: "Gateway Timeout" },
  505: {
    name: "http_version_not_supported",
    description: "HTTP Version Not Supported",
  },
  506: {
    name: "variant_also_negotiates",
    description: "Variant Also Negotiates",
  },
  507: { name: "insufficient_storage", description: "Insufficient Storage" },
  508: { name: "loop_detected", description: "Loop Detected" },
  509: {
    name: "bandwidth_limit_exceeded",
    description: "Bandwidth Limit Exceeded",
  },
  510: { name: "not_extended", description: "Not Extended" },
  511: {
    name: "network_authentication_required",
    description: "Network Authentication Required",
  },
};

// 500번대 에러 체크 유틸리티 함수
const is5xxError = (error) => {
  const status = error?.status || error?.statusCode || error?.response?.status;
  if (status && status >= 500 && status <= 599) {
    return status;
  }

  // 에러 메시지에서 상태 코드 찾기
  if (error?.message) {
    const match = error.message.match(/\b5\d{2}\b/);
    if (match) {
      return parseInt(match[0]);
    }
  }

  return null;
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate: 0.2,
  environment: process.env.REACT_APP_ENV || "development",

  beforeSend(event, hint) {
    const error = hint.originalException;
    let statusCode = null;

    if (error) {
      statusCode = error.status || error.statusCode || error.response?.status;

      if (!statusCode && error.message) {
        const match = error.message.match(/\b5\d{2}\b/);
        if (match) {
          statusCode = parseInt(match[0]);
        }
      }
    }

    if (statusCode && statusCode >= 500 && statusCode <= 599) {
      const errorType = SERVER_ERROR_TYPES[statusCode] || {
        name: `server_error_${statusCode}`,
        description: `Server Error ${statusCode}`,
      };

      event.tags = {
        ...event.tags,
        error_category: "5xx_error",
        error_type: errorType.name,
        http_status: statusCode.toString(),
        http_status_group: "5xx",
        severity: statusCode === 503 ? "warning" : "error",
      };

      event.contexts = {
        ...event.contexts,
        server_error: {
          status_code: statusCode,
          error_name: errorType.name,
          error_description: errorType.description,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          user_agent: navigator.userAgent,
        },
      };

      if (statusCode === 503 || statusCode === 504) {
        event.level = "warning";
      } else {
        event.level = "error";
      }

      if (error.config || error.response) {
        event.contexts.api_error = {
          endpoint: error.config?.url,
          method: error.config?.method,
          response_data: error.response?.data,
          response_headers: error.response?.headers,
        };
      }
    }

    return event;
  },

  ignoreErrors: [
    // 기존
    "ResizeObserver loop limit exceeded",
    "Non-Error promise rejection captured",

    // 네트워크 단절 (사용자 환경 문제, 코드로 해결 불가)
    "Failed to fetch",
    "Load failed", // Safari
    "NetworkError", // Firefox
    "Network request failed",
    "fetch is aborted",
    "The network connection was lost",

    // 타임아웃
    "TimeoutError",
    "Request timed out",

    // 브라우저 확장 프로그램
    /^chrome-extension:\/\//,
    /extensions\//i,

    // 기타 노이즈
    "Script error.",
  ],
});

const SentryErrorBoundary = Sentry.ErrorBoundary;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <SentryErrorBoundary
      fallback={({ error, resetError }) => {
        const statusCode = is5xxError(error);
        return (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h1>오류가 발생했습니다</h1>
            <p>오류: {error.message}</p>
            {statusCode && <p>상태 코드: {statusCode}</p>}
            <button onClick={resetError}>다시 시도</button>
          </div>
        );
      }}
      showDialog={process.env.REACT_APP_ENV === "production"}
      beforeCapture={(scope, error) => {
        const statusCode = is5xxError(error);
        if (statusCode) {
          const errorType = SERVER_ERROR_TYPES[statusCode] || {
            name: `server_error_${statusCode}`,
          };

          scope.setTag("error_category", "5xx_error");
          scope.setTag("error_type", errorType.name);
          scope.setTag("http_status", statusCode.toString());
        }
      }}
    >
      <RecoilRoot>
        <RouterProvider router={Routers} />
      </RecoilRoot>
    </SentryErrorBoundary>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
